import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, vShow as _vShow, withKeys as _withKeys, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-b00c0f2c"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "pagination-bar" }
const _hoisted_3 = ["tabindex"]
const _hoisted_4 = { class: "visually-hidden" }
const _hoisted_5 = ["tabindex"]
const _hoisted_6 = { class: "visually-hidden" }
const _hoisted_7 = ["onClick", "onKeydown"]
const _hoisted_8 = ["tabindex"]
const _hoisted_9 = { class: "visually-hidden" }
const _hoisted_10 = { "aria-hidden": "true" }
const _hoisted_11 = ["tabindex"]
const _hoisted_12 = { class: "visually-hidden" }
const _hoisted_13 = ["tabindex"]
const _hoisted_14 = { class: "visually-hidden" }

import { computed, ref, watch } from 'vue'
import { PageEntry } from './PaginationBar.interfaces'
import ChevronLeftIcon from '@/icons/ChevronLeftIcon.vue'
import ChevronRightIcon from '@/icons/ChevronRightIcon.vue'
import ChevronDoubleLeftIcon from '@/icons/ChevronLeftDoubleIcon.vue'
import ChevronDoubleRightIcon from '@/icons/ChevronRightDoubleIcon.vue'
import locales from './PaginationBar.locales.en.json'


export default /*#__PURE__*/_defineComponent({
  __name: 'PaginationBar',
  props: {
  paginationValue: {
    type: Number,
    required: true
  },
  totalPages: {
    type: Number,
    required: true
  },
  //Spread is the number of pages above and below the current page that will be selectable from the page label
  //ie: say totalPages = 10, currentPage = 5, and spread = 2 => page label will look like 1...3 4 5 6 7...10
  spread: {
    type: Number,
    default: 1
  },
  disabled: {
    type: Boolean
  },
  scrollToTop: {
    type: Boolean
  }
},
  emits: ['update:paginationValue'],
  setup(__props, { emit: __emit }) {

const props = __props

const emit = __emit

const internalPage = ref(props.paginationValue)
const showPrevious = computed<boolean>(
  (): boolean => internalPage.value > 1
)
const showNext = computed<boolean>(
  (): boolean => internalPage.value < props.totalPages
)

const entries = computed<Array<PageEntry>>((): Array<PageEntry> => {
  const retEntries = [] as Array<PageEntry>

  // first page not disabled if not selected
  if (internalPage.value !== 1) {
    retEntries.push({
      pageLabel: '1',
      class: 'hover-cursor',
      fillInType: null
    })
  }

  // preceding dots
  if (internalPage.value > props.spread + 2) {
    retEntries.push({
      pageLabel: '...',
      class: 'hover-cursor',
      fillInType: 'prev'
    })
  }

  // numbers spread behind current page
  let startIndex = Math.max(2, internalPage.value - props.spread)
  for (let i = startIndex; i < internalPage.value; i++) {
    retEntries.push({
      pageLabel: i.toString(),
      class: 'hover-cursor',
      fillInType: null
    })
  }

  // current page
  retEntries.push({
    pageLabel: internalPage.value.toString(),
    class: 'disabled',
    fillInType: null
  })

  // numbers spread ahead of current page
  const endIndex = Math.min(
    props.totalPages - 1,
    internalPage.value + props.spread
  )
  for (let i = internalPage.value + 1; i <= endIndex; i++) {
    retEntries.push({
      pageLabel: i.toString(),
      class: 'hover-cursor',
      fillInType: null
    })
  }

  // following dots
  if (internalPage.value < props.totalPages - props.spread - 1) {
    retEntries.push({
      pageLabel: '...',
      class: 'hover-cursor',
      fillInType: 'next'
    })
  }

  // final page not disabled if not on final page
  if (internalPage.value !== props.totalPages) {
    retEntries.push({
      pageLabel: props.totalPages.toString(),
      class: 'hover-cursor',
      fillInType: null
    })
  }

  return retEntries
})

const navigate = (entry: PageEntry): void => {
  let page = parseInt(entry.pageLabel)
  if (entry.fillInType === 'prev') {
    page = internalPage.value - props.spread - 1
  } else if (entry.fillInType === 'next') {
    page = internalPage.value + props.spread + 1
  }
  emit('update:paginationValue', page)
  doScrollToTop()
}

const navigatePrevious = (): void => {
  emit('update:paginationValue', internalPage.value - 1)
  doScrollToTop()
}

const navigateNext = (): void => {
  emit('update:paginationValue', internalPage.value + 1)
  doScrollToTop()
}

const navigateFirst = (): void => {
  emit('update:paginationValue', 1)
  doScrollToTop()
}

const navigateLast = (): void => {
  emit('update:paginationValue', props.totalPages)
  doScrollToTop()
}

function doScrollToTop() {
  if (props.scrollToTop) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

function createPageAriaLabel(entry: PageEntry): string {
  if (entry.pageLabel === '...') {
    if (entry.fillInType === 'prev') {
      return locales.label_goto_prev_set
    }
    if (entry.fillInType === 'next') {
      return locales.label_goto_prev_set
    }
  }
  return locales.label_goto_page.replace('{page}', entry.pageLabel)
}

watch(
  () => props.paginationValue,
  (newVal: number): void => {
    internalPage.value = newVal
  }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("nav", {
    "aria-label": _unref(locales).label_pagination
  }, [
    _createElementVNode("ul", _hoisted_2, [
      _withDirectives(_createElementVNode("li", {
        onClick: navigateFirst,
        onKeydown: [
          _withKeys(navigateFirst, ["enter"]),
          _withKeys(navigateFirst, ["space"])
        ]
      }, [
        _createElementVNode("a", {
          class: _normalizeClass(["pagination-bar__item", {
            'pagination-bar__item--disabled': __props.disabled
          }]),
          href: "#",
          tabindex: __props.disabled ? -1 : 0,
          onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["prevent"]))
        }, [
          _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(locales).button_first), 1),
          _createVNode(ChevronDoubleLeftIcon)
        ], 10, _hoisted_3)
      ], 544), [
        [_vShow, showPrevious.value]
      ]),
      _withDirectives(_createElementVNode("li", {
        onClick: navigatePrevious,
        onKeydown: [
          _withKeys(navigatePrevious, ["enter"]),
          _withKeys(navigatePrevious, ["space"])
        ]
      }, [
        _createElementVNode("a", {
          class: _normalizeClass(["pagination-bar__item", {
            'pagination-bar__item--disabled': __props.disabled
          }]),
          href: "#",
          tabindex: __props.disabled ? -1 : 0,
          onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"]))
        }, [
          _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(locales).button_prev), 1),
          _createVNode(ChevronLeftIcon)
        ], 10, _hoisted_5)
      ], 544), [
        [_vShow, showPrevious.value]
      ]),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(entries.value, (entry) => {
        return (_openBlock(), _createElementBlock("li", {
          key: entry.pageLabel,
          onClick: ($event: any) => (navigate(entry)),
          onKeydown: [
            _withKeys(($event: any) => (navigate(entry)), ["enter"]),
            _withKeys(($event: any) => (navigate(entry)), ["space"])
          ]
        }, [
          _createElementVNode("a", {
            class: _normalizeClass(["pagination-bar__item", {
            'pagination-bar__item--disabled': entry.disabled,
            'pagination-bar__item--selected': entry.pageLabel === __props.paginationValue.toString(),
          }]),
            href: "#",
            tabindex: __props.disabled ? -1 : 0,
            onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent"]))
          }, [
            _createElementVNode("span", _hoisted_9, _toDisplayString(createPageAriaLabel(entry)), 1),
            _createElementVNode("span", _hoisted_10, _toDisplayString(entry.pageLabel), 1)
          ], 10, _hoisted_8)
        ], 40, _hoisted_7))
      }), 128)),
      _withDirectives(_createElementVNode("li", {
        onClick: navigateNext,
        onKeydown: [
          _withKeys(navigateNext, ["enter"]),
          _withKeys(navigateNext, ["space"])
        ]
      }, [
        _createElementVNode("a", {
          class: _normalizeClass(["pagination-bar__item", {
            'pagination-bar__item--disabled': __props.disabled
          }]),
          href: "#",
          tabindex: __props.disabled ? -1 : 0,
          onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["prevent"]))
        }, [
          _createElementVNode("span", _hoisted_12, _toDisplayString(_unref(locales).button_next), 1),
          _createVNode(ChevronRightIcon)
        ], 10, _hoisted_11)
      ], 544), [
        [_vShow, showNext.value]
      ]),
      _withDirectives(_createElementVNode("li", {
        onClick: navigateLast,
        onKeydown: [
          _withKeys(navigateLast, ["enter"]),
          _withKeys(navigateLast, ["space"])
        ]
      }, [
        _createElementVNode("a", {
          class: _normalizeClass(["pagination-bar__item", {
            'pagination-bar__item--disabled': __props.disabled
          }]),
          href: "#",
          tabindex: __props.disabled ? -1 : 0,
          onClick: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["prevent"]))
        }, [
          _createElementVNode("span", _hoisted_14, _toDisplayString(_unref(locales).button_last), 1),
          _createVNode(ChevronDoubleRightIcon)
        ], 10, _hoisted_13)
      ], 544), [
        [_vShow, showNext.value]
      ])
    ])
  ], 8, _hoisted_1))
}
}

})